import PropTypes from "prop-types";
import React from "react";
import classNames from "classnames";
import "./FileUpload.scss";
import { AttachmentIcon, TrashBinIcon } from "wm-graphics";
import { Text } from "../Text";
import { withTranslations } from "../../../utils/withTranslations";

import defaultLocales from "./locales";

const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 500000000;
const KILO_BYTES_PER_BYTE = 1000;
const ENTER_KEY = "Enter";

export class UFileUpload extends React.Component {
  static defaultProps = {
    locales: {},
    maxFileSizeInBytes: DEFAULT_MAX_FILE_SIZE_IN_BYTES,
    multiple: false,
    showPreview: true,
    size: "large",
  };

  static propTypes = {
    /** Type of file to accept */
    accept: PropTypes.string,
    /** Additional class names for the component */
    className: PropTypes.string,
    /** Id associated with component */
    id: PropTypes.any,
    /** Label associated with component */
    label: PropTypes.string,
    /** Locales object for rendering text */
    locales: PropTypes.object,
    /** For preventing files above the specified size from being uploaded */
    maxFileSizeInBytes: PropTypes.number,
    /** If true allows for the upload of multiple images from computer at a time */
    multiple: PropTypes.bool,
    /** A callback function used for sending the files state to the parent component */
    onChange: PropTypes.func,
    /** Whether image previews should be shown */
    showPreview: PropTypes.bool,
    /** The size of the file upload component */
    size: PropTypes.oneOf(["large", "small"]),
  };

  constructor(props) {
    super(props);

    this.state = {
      files: {},
    };

    this.fileInputField = React.createRef();
    this.fieldContainer = React.createRef();
  }

  handleUploadBtnClick = () => {
    this.fileInputField.current.click();
  };

  handleNewFileUpload = (event) => {
    const { multiple, onChange } = this.props;
    const { files: newFiles } = event.target;

    if (newFiles.length) {
      let updatedFiles;
      if (!multiple) {
        updatedFiles = this.overwriteNewFiles([newFiles[0]]);
      } else {
        updatedFiles = this.appendNewFiles(newFiles);
      }

      this.setState({ files: updatedFiles });

      if (onChange) {
        this.callUpdateFilesCb(updatedFiles);
      }
    }
  };

  overwriteNewFiles = (newFiles) => {
    const files = {};
    const { maxFileSizeInBytes } = this.props;
    Object.values(newFiles).map((file) => {
      if (file.size <= maxFileSizeInBytes) {
        files[file.name] = file;
      }
      return null;
    });
    return { ...files };
  };

  appendNewFiles = (newFiles) => {
    const { files } = this.state;
    const { maxFileSizeInBytes } = this.props;
    Object.values(newFiles).map((file) => {
      if (file.size <= maxFileSizeInBytes) {
        files[file.name] = file;
      }
      return null;
    });
    return { ...files };
  };

  removeFile = (fileName) => {
    const { onChange } = this.props;
    const { files } = this.state;
    delete files[fileName];
    this.setState({ files: { ...files } });

    if (onChange) {
      this.callUpdateFilesCb({ ...files });
    }
  };

  onKeyDownHandler = (fileName, event) => {
    if (event.key === ENTER_KEY) {
      this.removeFile(fileName);
    }
  };

  convertNestedObjectToArray = (nestedObj) =>
    Object.keys(nestedObj).map((key) => nestedObj[key]);

  callUpdateFilesCb = (files) => {
    const { onChange } = this.props;
    const filesAsArray = this.convertNestedObjectToArray(files);

    onChange(filesAsArray);
  };

  convertBytesToKB = (bytes) => Math.round(bytes / KILO_BYTES_PER_BYTE);

  isImageFile = (file) => file.type.split("/")[0] === "image";

  onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({ draggingOver: true });
  };

  onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({ draggingOver: false });
  };

  isValidFileType = (file) => {
    const { accept } = this.props;
    if (!accept) return true;

    // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept#limiting_accepted_file_types

    const acceptedTypes = accept.split(",").map((type) => type.trim());

    return acceptedTypes.some((acceptedType) => {
      // If the acceptedType ends with "/*" which in MIME types means any subtype is acceptable ("image/*" means all image MIME types),
      // it checks if the file's MIME type starts with the same primary type.
      if (acceptedType.endsWith("/*")) {
        return file.type.startsWith(acceptedType.slice(0, -2));
      }
      // If the acceptedType includes a "/", it is a full MIME type (like "image/png" or "text/plain").
      // Checks if the file's MIME type exactly matches this type.
      if (acceptedType.includes("/")) {
        return file.type === acceptedType;
      }
      // If none of the above conditions were met, the acceptedType is probably a file extension (like ".png" or ".txt").
      // Checks if the file's name ends with this extension (case-insensitive).
      return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
    });
  };

  onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = [];
    if (event.dataTransfer.items) {
      Array.from(event.dataTransfer.items).forEach((item) => {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (this.isValidFileType(file)) {
            files.push(file);
          }
        }
      });
    } else {
      Array.from(event.dataTransfer.files).forEach((file) => {
        if (this.isValidFileType(file)) {
          files.push(file);
        }
      });
    }
    this.handleNewFileUpload({ target: { files } });
    this.setState({ draggingOver: false });
  };

  render() {
    const {
      accept,
      className,
      id,
      label,
      locales,
      multiple,
      showPreview,
      size,
    } = this.props;
    const { draggingOver, files } = this.state;

    const fileUploadClass = classNames("file", size, className, {
      draggingOver,
    });

    const filesHaveBeenChosen = Object.keys(files).length > 0;

    return (
      <div
        className={fileUploadClass}
        id={id}
        label={label}
        ref={this.fieldContainer}
      >
        <div
          className="upload__container"
          data-testid="upload__container"
          onDragOver={this.onDragOver}
          onDragLeave={this.onDragLeave}
          onDrop={this.onDrop}
        >
          <Text
            type="P"
            weight="Light"
            className="upload__container__description"
          >
            {multiple ? locales.descriptionMultiple : locales.description}
          </Text>
          <button
            className="upload__container__button"
            type="button"
            onClick={this.handleUploadBtnClick}
          >
            <i>
              <AttachmentIcon className="add" />
            </i>
            <Text type="P" weight="Light">
              {multiple ? locales.uploadMultiple : locales.upload}
            </Text>
          </button>
          <input
            accept={accept}
            className="upload__container__input"
            data-testid="upload__container__input"
            type="file"
            title=""
            value=""
            ref={this.fileInputField}
            onChange={this.handleNewFileUpload}
            multiple={multiple}
          />
        </div>
        {showPreview && (
          <article className="preview__container">
            {filesHaveBeenChosen && <span>{locales.chosen}</span>}
            <section className="preview__container__list">
              {Object.keys(files).map((fileName, index) => {
                const file = files[fileName];
                return (
                  <section
                    className="preview__container__list__item"
                    key={fileName}
                  >
                    <div>
                      {this.isImageFile(file) && (
                        <img
                          className="preview__container__list__item__image"
                          src={URL.createObjectURL(file)}
                          alt={`file preview ${index}`}
                        />
                      )}
                      <div className="preview__container__list__item__metadata">
                        <span className="preview__container__list__item__name">
                          {file.name}
                        </span>
                        <aside>
                          <span>{this.convertBytesToKB(file.size)} KB</span>
                          <i
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) =>
                              this.onKeyDownHandler(fileName, event)
                            }
                            onClick={() => this.removeFile(fileName)}
                          >
                            {" "}
                            <TrashBinIcon className="trash" fill="white" />{" "}
                          </i>
                        </aside>
                      </div>
                    </div>
                  </section>
                );
              })}
            </section>
          </article>
        )}
      </div>
    );
  }
}

const TranslatedFileUpload = withTranslations(defaultLocales)(UFileUpload);

export { TranslatedFileUpload as FileUpload };

export default TranslatedFileUpload;
